/**
 * SCS fallback for Stamped review-media clicks.
 *
 * Why this exists (2026-07-29): Stamped's
 * /api/widget/reviews?type=widget-carousel-photos endpoint returns HTTP 500
 * for the SCS account, so StampedFn.openUGCPhoto() never creates its modal.
 *
 * This script intercepts only Stamped review photo/video clicks and renders an
 * accessible, in-page lightbox. It does not modify review loading, sorting, or
 * pagination, and it does not open raw CDN media in a new tab.
 */
(() => {
  'use strict';

  if (window.SCSReviewLightbox) return;

  const MEDIA_SELECTOR = '[onclick*="StampedFn.openUGCPhoto"]';
  const ROOT_ID = 'scs-review-lightbox';
  const STYLE_ID = 'scs-review-lightbox-styles';
  let lastTrigger = null;
  let priorBodyOverflow = '';
  let priorBodyPaddingRight = '';
  let observer = null;

  const styles = `
    #${ROOT_ID}[hidden] { display: none !important; }
    #${ROOT_ID} {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: grid;
      place-items: center;
      padding: 24px;
      font-family: Roboto, Arial, sans-serif;
    }
    #${ROOT_ID} .scs-review-lightbox__backdrop {
      position: absolute;
      inset: 0;
      display: block !important;
      background: rgba(10, 10, 11, 0.88);
      cursor: zoom-out;
    }
    #${ROOT_ID} .scs-review-lightbox__dialog {
      position: relative;
      z-index: 1;
      width: min(1100px, 100%);
      max-height: calc(100vh - 48px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border-radius: 6px;
      background: #0A0A0B;
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
      outline: none;
    }
    #${ROOT_ID} .scs-review-lightbox__header {
      min-height: 64px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 10px 10px 10px 20px;
      background: #FFFFFF;
      color: #0A0A0B;
    }
    #${ROOT_ID} .scs-review-lightbox__title {
      margin: 0;
      font-family: Oswald, Arial, sans-serif;
      font-size: 1.1rem;
      font-weight: 600;
      line-height: 1.2;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    #${ROOT_ID} .scs-review-lightbox__close {
      flex: 0 0 auto;
      width: 44px;
      height: 44px;
      display: inline-grid;
      place-items: center;
      border: 0;
      border-radius: 999px;
      background: #ED1B34;
      color: #FFFFFF;
      cursor: pointer;
      font: 700 28px/1 Arial, sans-serif;
    }
    #${ROOT_ID} .scs-review-lightbox__close:hover { background: #CF1A2D; }
    #${ROOT_ID} .scs-review-lightbox__close:active { background: #A41323; }
    #${ROOT_ID} .scs-review-lightbox__close:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(237, 27, 52, 0.25), 0 0 0 5px #FFFFFF;
    }
    #${ROOT_ID} .scs-review-lightbox__media {
      min-height: 240px;
      display: grid;
      place-items: center;
      overflow: auto;
      background: #0A0A0B;
    }
    #${ROOT_ID} .scs-review-lightbox__media img,
    #${ROOT_ID} .scs-review-lightbox__media video {
      display: block;
      max-width: 100%;
      max-height: calc(100vh - 112px);
      width: auto;
      height: auto;
      object-fit: contain;
      background: #0A0A0B;
    }
    #${ROOT_ID} .scs-review-lightbox__media video { width: 100%; }
    #${ROOT_ID} .scs-review-lightbox__error {
      margin: 0;
      padding: 48px 24px;
      color: #FFFFFF;
      text-align: center;
    }
    ${MEDIA_SELECTOR} { cursor: zoom-in; }
    ${MEDIA_SELECTOR}:focus-visible {
      outline: 3px solid #ED1B34;
      outline-offset: 3px;
    }
    @media (max-width: 640px) {
      #${ROOT_ID} { padding: 0; }
      #${ROOT_ID} .scs-review-lightbox__dialog {
        width: 100%;
        max-height: 100dvh;
        min-height: 100dvh;
        border-radius: 0;
      }
      #${ROOT_ID} .scs-review-lightbox__header { min-height: 60px; padding-left: 16px; }
      #${ROOT_ID} .scs-review-lightbox__media { flex: 1; min-height: 0; }
      #${ROOT_ID} .scs-review-lightbox__media img,
      #${ROOT_ID} .scs-review-lightbox__media video { max-height: calc(100dvh - 60px); }
    }
    @media (prefers-reduced-motion: reduce) {
      #${ROOT_ID} *, #${ROOT_ID} *::before, #${ROOT_ID} *::after {
        scroll-behavior: auto !important;
        transition: none !important;
        animation: none !important;
      }
    }
  `;

  function createUi() {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = styles;
      document.head.appendChild(style);
    }

    const root = document.createElement('div');
    root.id = ROOT_ID;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = `
      <div class="scs-review-lightbox__backdrop" data-scs-review-close></div>
      <section class="scs-review-lightbox__dialog" role="dialog" aria-modal="true"
        aria-labelledby="scs-review-lightbox-title" tabindex="-1">
        <header class="scs-review-lightbox__header">
          <h2 class="scs-review-lightbox__title" id="scs-review-lightbox-title">Customer media</h2>
          <button class="scs-review-lightbox__close" type="button"
            aria-label="Close customer media" data-scs-review-close>&times;</button>
        </header>
        <div class="scs-review-lightbox__media" aria-live="polite"></div>
      </section>`;
    document.body.appendChild(root);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-scs-review-close]')) close();
    });
    root.addEventListener('keydown', trapFocus);
    return root;
  }

  const root = createUi();
  const dialog = root.querySelector('.scs-review-lightbox__dialog');
  const title = root.querySelector('.scs-review-lightbox__title');
  const mediaHolder = root.querySelector('.scs-review-lightbox__media');
  const closeButton = root.querySelector('.scs-review-lightbox__close');

  function getTrigger(eventTarget) {
    return eventTarget instanceof Element ? eventTarget.closest(MEDIA_SELECTOR) : null;
  }

  function getImageElement(trigger) {
    return trigger.matches('img') ? trigger : trigger.querySelector('img');
  }

  function mediaInfo(trigger) {
    const image = getImageElement(trigger);
    const poster = image?.getAttribute('src') || image?.dataset.src || image?.currentSrc || '';
    const href = trigger.matches('a') ? trigger.getAttribute('href') : trigger.closest('a')?.getAttribute('href');
    const classContext = `${trigger.className || ''} ${trigger.closest('.stamped-video')?.className || ''}`;
    const isVideo = /\/uploads\/videos\//i.test(poster) || /stamped-video/i.test(classContext);
    const alt = image?.getAttribute('alt') || image?.getAttribute('aria-label') || 'Customer-submitted review media';
    let src = href || poster;

    if (isVideo) {
      src = poster.replace(/\.jpe?g(?:\?.*)?$/i, '.mp4');
    } else if (src) {
      try {
        const url = new URL(src, location.href);
        // The carousel may expose a 180px thumbnail transform. Request the
        // original asset; review-body anchors already point to an 800px image.
        if (!trigger.matches('a')) url.searchParams.delete('tr');
        src = url.href;
      } catch (_) {}
    }

    const authorMatch = alt.match(/Customer (?:Photo|Video) From (.+)$/i);
    const kind = isVideo ? 'video' : 'photo';
    return {
      type: isVideo ? 'video' : 'image',
      src,
      poster,
      alt,
      title: authorMatch ? `Customer ${kind} from ${authorMatch[1]}` : `Customer ${kind}`
    };
  }

  function showError() {
    mediaHolder.replaceChildren();
    const message = document.createElement('p');
    message.className = 'scs-review-lightbox__error';
    message.textContent = 'This customer media could not be loaded. Please close the viewer and try again.';
    mediaHolder.appendChild(message);
  }

  function open(trigger) {
    const info = mediaInfo(trigger);
    if (!info.src) return;

    lastTrigger = trigger;
    title.textContent = info.title;
    mediaHolder.replaceChildren();

    let media;
    if (info.type === 'video') {
      media = document.createElement('video');
      media.controls = true;
      media.playsInline = true;
      media.preload = 'metadata';
      media.poster = info.poster;
      media.setAttribute('aria-label', info.alt);
      const source = document.createElement('source');
      source.src = info.src;
      source.type = 'video/mp4';
      media.appendChild(source);
      media.addEventListener('error', showError, { once: true });
    } else {
      media = document.createElement('img');
      media.src = info.src;
      media.alt = info.alt;
      media.decoding = 'async';
      media.addEventListener('error', showError, { once: true });
    }
    mediaHolder.appendChild(media);

    priorBodyOverflow = document.body.style.overflow;
    priorBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.style.overflow = 'hidden';

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    closeButton.focus({ preventScroll: true });
  }

  function close() {
    if (root.hidden) return;
    const video = mediaHolder.querySelector('video');
    if (video) video.pause();
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    mediaHolder.replaceChildren();
    document.body.style.overflow = priorBodyOverflow;
    document.body.style.paddingRight = priorBodyPaddingRight;
    lastTrigger?.focus?.({ preventScroll: true });
    lastTrigger = null;
  }

  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusables = [...dialog.querySelectorAll(
      'button:not([disabled]), video[controls], [href], [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusables.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function enhance(container = document) {
    container.querySelectorAll?.(MEDIA_SELECTOR).forEach((element) => {
      if (!element.hasAttribute('tabindex') || element.getAttribute('tabindex') === '-1') {
        element.setAttribute('tabindex', '0');
      }
      if (!element.matches('a, button')) element.setAttribute('role', 'button');
      element.setAttribute('aria-haspopup', 'dialog');
      element.removeAttribute('aria-hidden');
    });
  }

  function interceptClick(event) {
    const trigger = getTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }

  function interceptKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const trigger = getTrigger(event.target);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }

  function interceptKeyup(event) {
    const trigger = getTrigger(event.target);
    if (!trigger) return;
    // Stamped's inline onkeyup handler is not key-filtered: it calls the
    // failing API after *any* key, including Escape after focus is restored.
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape' && !root.hidden) {
      event.preventDefault();
      close();
    }
  }

  document.addEventListener('click', interceptClick, true);
  document.addEventListener('keydown', interceptKeydown, true);
  document.addEventListener('keyup', interceptKeyup, true);
  document.addEventListener('keydown', onDocumentKeydown);
  enhance();

  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(MEDIA_SELECTOR)) enhance(node.parentElement || document);
        else enhance(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.SCSReviewLightbox = {
    open,
    close,
    destroy() {
      close();
      observer?.disconnect();
      document.removeEventListener('click', interceptClick, true);
      document.removeEventListener('keydown', interceptKeydown, true);
      document.removeEventListener('keyup', interceptKeyup, true);
      document.removeEventListener('keydown', onDocumentKeydown);
      root.remove();
      document.getElementById(STYLE_ID)?.remove();
      delete window.SCSReviewLightbox;
    }
  };
})();
